import {
  AMBIENT_SCRIBE_NOTE_PARENT_TYPE,
  type AmbientScribeNoteError,
  type AmbientScribeNoteParentType
} from "services/types/ambient-scribe-note.type";

/**
 * User-facing copy for a failed generated note — audiology summaries and chart-note SOAP alike
 * (SN-19067, doc §10).
 *
 * These are not HTTP errors: the request returned `202` and the backend recorded a failure row, so
 * they render as actionable states rather than "something went wrong".
 *
 * **Only the audiology path sets `error.code`.** A SOAP failure carries either
 * `{provider_message, provider_error_type}` or `{message}` — see `AmbientScribeNoteError`. So the
 * code map below is effectively audiology-only, and SOAP failures always take the unknown branch.
 *
 * `report_has_no_clinical_data` was REMOVED backend-side along with the refusal it described, and is
 * deliberately absent — an unknown code falls through to the generic line, which is right if an
 * older backend ever sends it.
 */

const GENERIC_MESSAGE_BY_PARENT_TYPE: Record<
  AmbientScribeNoteParentType,
  string
> = {
  [AMBIENT_SCRIBE_NOTE_PARENT_TYPE.AUDIOLOGY_REPORT]:
    "The report summary couldn't be generated. Please try again in a few moments.",
  [AMBIENT_SCRIBE_NOTE_PARENT_TYPE.CHART_NOTE]:
    "The scribe note couldn't be generated. Please try again in a few moments.",
  [AMBIENT_SCRIBE_NOTE_PARENT_TYPE.AFTER_VISIT_SUMMARY]:
    "The After Visit Summary couldn't be generated. Please try again in a few moments."
};

const MESSAGES_BY_CODE: Record<string, string> = {
  no_audiology_report:
    "Create the report for this visit before generating the summary.",
  report_not_found: "That report is no longer available. Pick another.",
  no_transcript: "This recording has no transcript yet.",
  audiology_context_failed:
    "Reading the audiology data failed. Please try generating the summary again."
};

export type ScribeNoteErrorDisplay = {
  /** Safe to show a clinician. Never carries provider internals. */
  message: string;
  /**
   * Raw provider text, for a collapsible disclosure. Undefined when the message above already says
   * everything there is to say.
   */
  detail?: string;
};

export function getScribeNoteErrorDisplay(
  error: AmbientScribeNoteError | null | undefined,
  parentType: AmbientScribeNoteParentType
): ScribeNoteErrorDisplay {
  const generic = GENERIC_MESSAGE_BY_PARENT_TYPE[parentType];
  if (!error) return { message: generic };

  const known = error.code ? MESSAGES_BY_CODE[error.code] : undefined;
  if (known) return { message: known };

  // Unknown code, or none at all — a provider/model failure. The raw text is genuinely useful to
  // support, but it is a stack-shaped string ("litellm.APIError: OpenAIException - Error code: 402
  // … Received Model Group=gpt-5.4"), so it goes behind a disclosure rather than being shown as the
  // message. Rendering it verbatim, which this used to do, put provider internals in front of a
  // clinician on every model outage.
  const detail = (error.message ?? error.provider_message)?.trim();
  return { message: generic, ...(detail ? { detail } : {}) };
}
